import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { SpacingType, getSpacing } from '@omni/kit/theming/SpacingType';
import { View, ViewStyle } from 'react-native';

import Colors from '@omni/kit/theming/Colors';
import Depth from '@omni/kit/theming/Depth';
import { IBlockProps } from './types';
import { IconName } from '@omni/kit/components/KitIcon';
import KitAvatar from '@omni/kit/components/KitAvatar';
import { KitListItem } from '@omni/kit/components';
import React from 'react';
import { dispatch } from '@omni/kit/ActionHandler';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';

const debug = require('debug')('tca:blocks:AvatarRowBlock');

export interface IAvatarRowBlockProps extends IBlockProps {
  /** An action that will occur when a user taps/clicks on the block.*/
  action?: any;

  title: string;
  titleColor?: string;
  titleFontSize?: number;
  isTitleBold: boolean;
  subtitle: string;
  subtitleColor?: string;
  subtitleFontSize?: number;
  hoverStyle?: ViewStyle;
  scaleOnTouch: boolean;
  hasBottomBorder: boolean;
  topPadding: SpacingType;
  bottomPadding: SpacingType;
  imageUrl?: string;
  nickname: string;
  avatarSize: number;
  rightIcon?: IconName | '';
  showRightIconOnAndroid?: boolean;
}

export default function AvatarRowBlock(
  props: IAvatarRowBlockProps
): JSX.Element {
  const {
    action = null,
    insetStyle = {},
    title = '',
    titleColor,
    titleFontSize,
    isTitleBold = false,
    subtitle = '',
    subtitleColor,
    subtitleFontSize,
    topSpacing = null,
    bottomSpacing = null,
    topPadding = null,
    bottomPadding = null,
    horizontalSpacing,
    scaleOnTouch = false,
    hasBottomBorder = true,
    imageUrl = '',
    nickname = '',
    avatarSize = 48,
    rightIcon = 'arrow-right',
    showRightIconOnAndroid = true,
    hoverStyle,
  } = props;

  const { edgeSpacing } = useScreenContext({
    fixedSpacingType: horizontalSpacing,
  });

  const viewStyle = {
    marginTop: getSpacing(topSpacing),
    marginBottom: getSpacing(bottomSpacing),
  };

  const avatarViewStyle = {
    paddingTop: getSpacing(topPadding),
    paddingBottom: getSpacing(bottomPadding),
  };

  const { sizeClass } = useSizeClass();

  const horizontalPaddingValue = () => {
    const small = sizeClass === SizeClass.Small;

    return small
      ? edgeSpacing
      : ((insetStyle.padding as number) ?? 0) + edgeSpacing;
  };

  const dispatchAction = () => {
    dispatch(action);
  };

  return (
    <View style={viewStyle}>
      <KitListItem
        title={title}
        titleColor={titleColor}
        titleFontSize={titleFontSize}
        isTitleBold={isTitleBold}
        subtitle={subtitle}
        subtitleColor={subtitleColor}
        subtitleFontSize={subtitleFontSize}
        bottomBorder={hasBottomBorder}
        scaleOnTouch={scaleOnTouch}
        rightIcon={rightIcon}
        hoverStyle={hoverStyle}
        showRightIconOnAndroid={showRightIconOnAndroid}
        style={{
          paddingHorizontal: horizontalPaddingValue(),
        }}
        ImageComponent={
          <View style={[avatarViewStyle, Depth.m]}>
            <KitAvatar
              imageUrl={imageUrl}
              nickname={nickname}
              size={avatarSize}
            />
          </View>
        }
        onPress={action ? dispatchAction : undefined}
        onPressUnderlayColor={Colors.N50}
      />
    </View>
  );
}
